/**
 * Copies text to the clipboard and reports whether it worked.
 *
 * `navigator.clipboard` is unavailable outside secure contexts (http on a LAN IP,
 * for example) and rejects when the document is not focused or the user gesture
 * already expired, which happens whenever the copy runs after an awaited request.
 * The legacy `execCommand` path has none of those restrictions, so it is used as
 * a fallback before giving up.
 */
const copyToClipboard = async (text: string): Promise<boolean> => {
  if (!text) return false;

  try {
    if (window.isSecureContext && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Not focused, gesture expired or permission denied: try the fallback below.
  }

  try {
    const textarea = document.createElement('textarea');

    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.opacity = '0';

    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, text.length);

    const copied = document.execCommand('copy');

    document.body.removeChild(textarea);

    return copied;
  } catch {
    return false;
  }
};

export default copyToClipboard;
